import { apiClient } from './client';
import type {
  PricingTiersResponse,
  PriceCalculationResponse,
  CreatePricingTierData,
  UpdatePricingTierData,
  BulkUpdateTiersData,
  WinchPriceCalculationResponse,
} from '@/types';

/**
 * Winch Pricing API Client
 * Provides methods for interacting with winch distance-based pricing endpoints
 */
export const winchPricingAPI = {
  /**
   * Get all pricing tiers for a service
   */
  getPricingTiers: (serviceId: number) =>
    apiClient.get<PricingTiersResponse>(`/services/${serviceId}/pricing-tiers`),

  /**
   * Calculate price for a given distance
   */
  calculatePrice: (serviceId: number, distance: number) =>
    apiClient.get<PriceCalculationResponse>(`/services/${serviceId}/calculate-price`, {
      params: { distance },
    }),

  /**
   * Calculate price based on regions (with start point → pickup → destination)
   * Uses the active winch start point for total distance calculation
   */
  calculatePriceByRegions: (data: {
    serviceId: number;
    pickupRegionId: number;
    destinationRegionId: number;
  }) =>
    apiClient.post<WinchPriceCalculationResponse>(
      '/winch-pricing/calculate-by-regions',
      data
    ),

  /**
   * Calculate price using custom coordinates
   * Uses the active winch start point for total distance calculation
   */
  calculatePriceByCoordinates: (data: {
    serviceId: number;
    pickupLatitude: number;
    pickupLongitude: number;
    destinationLatitude: number;
    destinationLongitude: number;
  }) =>
    apiClient.post<WinchPriceCalculationResponse>(
      '/winch-pricing/calculate-by-coordinates',
      data
    ),

  /**
   * Create a new pricing tier (Admin only)
   */
  createPricingTier: (serviceId: number, data: CreatePricingTierData) =>
    apiClient.post(`/services/${serviceId}/pricing-tiers`, data),

  /**
   * Update an existing pricing tier (Admin only)
   */
  updatePricingTier: (tierId: number, data: UpdatePricingTierData) =>
    apiClient.put(`/pricing-tiers/${tierId}`, data),

  /**
   * Delete a pricing tier (Admin only)
   */
  deletePricingTier: (tierId: number) =>
    apiClient.delete(`/pricing-tiers/${tierId}`),

  /**
   * Bulk update pricing tiers (Admin only)
   */
  bulkUpdateTiers: (serviceId: number, data: BulkUpdateTiersData) =>
    apiClient.post(`/services/${serviceId}/pricing-tiers/bulk`, data),

  /**
   * Toggle pricing tier active status (Admin only)
   */
  toggleTierStatus: (tierId: number) =>
    apiClient.patch(`/pricing-tiers/${tierId}/toggle`),
};
